import React, { useEffect, useState, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { usePinManagement } from '@/hooks/usePinManagement';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import Dashboard from '@/pages/Dashboard';

export const DashboardWrapper = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { checkLockoutStatus } = usePinManagement();
  const { trackAction } = useActivityTracking(); // Initialize activity tracking
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);
  const didRunRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    // Prevent double-run in React Strict Mode
    if (didRunRef.current) return;
    didRunRef.current = true;

    const checkOnboardingStatus = async () => {

      try {
        const status = await checkLockoutStatus();
        
        // Only redirect to onboarding if explicitly needs it AND has no PIN
        if (status.needsOnboarding && !status.hasPin) {
          console.log('User needs onboarding, redirecting...');
          trackAction('Redirected to onboarding', { reason: 'no_pin' });
          navigate('/onboarding');
          return;
        }
        
        // Check for forced PIN change
        const shouldForceChange = status.requiresPinChange || localStorage.getItem('forceChangePin') === 'true';
        if (shouldForceChange) {
          localStorage.removeItem('forceChangePin');
          // Dispatch event to trigger forced PIN change dialog
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('forceChangePinAction'));
          }, 100);
        }
        
        setIsCheckingOnboarding(false);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // On error, fail open - show dashboard instead of forcing onboarding
        setIsCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user, navigate, checkLockoutStatus]);

  if (isCheckingOnboarding) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="relative z-10 text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Checking account status...</p>
        </div>
      </div>
    );
  }

  return <Dashboard />;
};